import type {
  BookState,
  BookUpdatePayload,
  Bookmark,
  CloudUpdate,
  Comment,
  DTO,
  PlatformType,
  ReadStatEvent,
  ReaderState,
  TagState,
  TagUpdate,
  UserState,
  ViewState,
} from '@readcloud/common';
import { LoadingIcon } from '@readcloud/component-library';
import type { Cloud, User } from '@readcloud/data';
import {
  authSelectors,
  bookSelectors,
  cloudSelectors,
  tagsSelectors,
} from '@root/libs/state/src';
import { Buffer } from 'buffer';
import React from 'react';
import { useSelector } from 'react-redux';
import { Observable, Subject } from 'rxjs';
import { environment } from '../environments/environment';

interface NativeToWebMessageTypePayloads {
  DUMMY: { name?: string };
  NETWORK_STATE_UPDATE: { online: boolean };
  BOOKS_STATE_UPDATE: BookState[];
  CLOUD_STATE_UPDATE: Cloud[];
  TAG_STATE_UPDATE: TagState[];
  USER_STATE_UPDATE: UserState;
  READER_STATE_UPDATE: ReaderState;
  CONTENT_STATE_UPDATE: any;
  BOOK_ANNOTATIONS_STATE_UPDATE: {
    bookId: string;
    annotations: DTO.Annotation[];
  };
  COMMENTS_STATE_UPDATE: Comment[];
  BOOKMARKS_STATE_UPDATE: Bookmark[];
  KNOWN_USERS_STATE_UPDATE: User[];
  USERPREF_STATE_UPDATE: DTO.UserPrefState;
  APP_CONFIG_STATE_UPDATE: any;
}

interface WebToNativeMessageTypePayloads {
  SEND_INFO: {
    platform: PlatformType;
    version: string;
    componentName?: string;
  };
  READY: {
    component?: 'READER' | 'CONTENT' | undefined;
  };
  CREATE_ANNOTATION: DTO.Annotation;
  DELETE_ANNOTATION: string;
  UPDATE_ANNOTATION: DTO.Annotation;
  CREATE_BOOKMARK: Bookmark;
  DELETE_BOOKMARK: string;
  UPDATE_BOOKMARK: Bookmark;
  CREATE_COMMENT: Comment;
  DELETE_COMMENT: string;
  UPDATE_COMMENT: Comment;
  FETCH_ALL: undefined;
  UPDATE_VIEW_STATE: ViewState;
  UPDATE_USERPREF_STATE: DTO.UserPrefState;

  UPDATE_CONTENT_STATE: any;
  HOME: undefined;
  UPDATE_TAG: TagUpdate;
  UPDATE_BOOK: BookUpdatePayload;
  UPDATE_CLOUD: CloudUpdate;
  CREATE_READSTAT: ReadStatEvent;
  SET_SELECTION_MENU: { enabled: boolean };
  SET_TOUCH_MENU: { enabled: boolean };
  RELOAD: undefined;
  IOS_URL_INTERCEPTOR_ON: undefined;
  IOS_URL_INTERCEPTOR_OFF: undefined;
  PRINT_HTML: string;
}

type NativeToWebEventStreams = {
  [k in keyof NativeToWebMessageTypePayloads]: Observable<
    NativeToWebMessageTypePayloads[k]
  >;
};

type ObservableToSubject<O extends Observable<any>> = O extends Observable<
  infer T
>
  ? Subject<T>
  : never;

type NativeToWebSubjects = {
  [k in keyof NativeToWebEventStreams]: ObservableToSubject<
    NativeToWebEventStreams[k]
  >;
};
type NativeToWebObservables = {
  [k in keyof NativeToWebEventStreams]: NativeToWebEventStreams[k];
};
type WebToNativeSubjects = {
  [k in keyof WebToNativeMessageTypePayloads]: Subject<
    WebToNativeMessageTypePayloads[k]
  >;
};

const makeMockNativeToWebSubjects = (): NativeToWebSubjects => {
  return {
    BOOK_ANNOTATIONS_STATE_UPDATE: new Subject(),
    DUMMY: new Subject(),
    USER_STATE_UPDATE: new Subject(),
    READER_STATE_UPDATE: new Subject(),
    NETWORK_STATE_UPDATE: new Subject(),
    BOOKS_STATE_UPDATE: new Subject(),
    CLOUD_STATE_UPDATE: new Subject(),
    KNOWN_USERS_STATE_UPDATE: new Subject(),
    TAG_STATE_UPDATE: new Subject(),
    BOOKMARKS_STATE_UPDATE: new Subject(),
    COMMENTS_STATE_UPDATE: new Subject(),
    CONTENT_STATE_UPDATE: new Subject(),
    USERPREF_STATE_UPDATE: new Subject(),
    APP_CONFIG_STATE_UPDATE: new Subject(),
  };
};

const makeMockWebToNativeEvents = (): WebToNativeSubjects => {
  return {
    FETCH_ALL: new Subject(),
    SEND_INFO: new Subject(),
    CREATE_ANNOTATION: new Subject(),
    DELETE_ANNOTATION: new Subject(),
    UPDATE_ANNOTATION: new Subject(),
    CREATE_COMMENT: new Subject(),
    DELETE_COMMENT: new Subject(),
    UPDATE_COMMENT: new Subject(),
    CREATE_BOOKMARK: new Subject(),
    DELETE_BOOKMARK: new Subject(),
    UPDATE_BOOKMARK: new Subject(),
    READY: new Subject(),
    UPDATE_VIEW_STATE: new Subject(),
    UPDATE_USERPREF_STATE: new Subject(),
    UPDATE_CONTENT_STATE: new Subject(),
    HOME: new Subject(),
    UPDATE_TAG: new Subject(),
    UPDATE_BOOK: new Subject(),
    UPDATE_CLOUD: new Subject(),
    CREATE_READSTAT: new Subject(),
    SET_SELECTION_MENU: new Subject(),
    SET_TOUCH_MENU: new Subject(),
    RELOAD: new Subject(),
    IOS_URL_INTERCEPTOR_ON: new Subject(),
    IOS_URL_INTERCEPTOR_OFF: new Subject(),
    PRINT_HTML: new Subject(),
  };
};

const fromContent = makeMockWebToNativeEvents();
const toContent = makeMockNativeToWebSubjects();

export function ContentPage(props: { navigateToCloudsView: () => void }) {
  const clouds = useSelector(cloudSelectors.getClouds);
  const user = useSelector(authSelectors.getUser);
  const books = useSelector(bookSelectors.getBooks);
  const tags = useSelector(tagsSelectors.getTags);
  const [contentReady, setContentReady] = React.useState(false);
  const [iframeReady, setIframeReady] = React.useState(false);
  const ready = contentReady && iframeReady;
  /*
    These are the hooks that send data to the iframe whenever our state changes and Content is ready.
  */
  React.useEffect(() => {
    if (ready) {
      toContent.CLOUD_STATE_UPDATE.next(Object.values(clouds));
    }
  }, [clouds, ready]);
  React.useEffect(() => {
    if (ready) {
      // @ts-expect-error
      toContent.USER_STATE_UPDATE.next(user);
    }
  }, [user, ready]);
  React.useEffect(() => {
    if (ready) {
      // @ts-expect-error
      toContent.BOOKS_STATE_UPDATE.next(books);
    }
  }, [books, ready]);
  React.useEffect(() => {
    if (ready) {
      toContent.TAG_STATE_UPDATE.next(tags);
    }
  }, [tags, ready]);

  /*
    This effect is responsible for setting our ready state to true when content is ready to receive messages.
  */
  React.useEffect(() => {
    const subscription = fromContent.READY.subscribe(() => {
      setContentReady(true);
    });
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  /*
    This effect is responsible for receiving the HOME event from Content, which is a signal to go back to the clouds view.
  */
  React.useEffect(() => {
    const subscription = fromContent.HOME.subscribe(() => {
      props.navigateToCloudsView();
    });
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  /*
    This effect is responsible for listening to and parsing messages from Content and dispatching them to the appropriate subject.
  */
  React.useEffect(() => {
    const listener = (e: MessageEvent<unknown>) => {
      try {
        if (typeof e.data === 'string') {
          const parsedEvent = JSON.parse(
            Buffer.from(e.data, 'base64').toString()
          );
          fromContent[parsedEvent.type]?.next(parsedEvent.payload);
        }
      } catch (e) {}
    };
    window.addEventListener('message', listener);
    return () => {
      window.removeEventListener('message', listener);
    };
  }, []);

  /*
    This callback is responsible for subscribing to events fired by "toContent", which is a map of RXJS subjects that are fired by us, then sending those events to Content.
  */
  const onLoad = React.useCallback(
    (e: React.SyntheticEvent<HTMLIFrameElement>) => {
      const iframe = e.target;
      if (
        !iframe ||
        !(iframe instanceof HTMLIFrameElement) ||
        !iframe.contentWindow
      )
        throw Error(
          "Invalid content iframe loading sequence - onLoad event fired with no IFrame as it's target"
        );

      Object.entries(toContent).forEach(([type, subject]) => {
        subject.subscribe((payload) => {
          iframe.contentWindow.postMessage({ type, payload }, '*');
        });
      });
      setIframeReady(true);
    },
    []
  );

  return (
    <div className="rc-realtive rc-h-full rc-w-full">
      <iframe
        className="rc-border-none rc-p-0 rc-m-0 rc-outline-none rc-w-full rc-h-full"
        src={`https://${environment.content_url}/?version=web&platform=android`}
        onLoad={onLoad}
      />
      {!iframeReady && (
        <div className="rc-absolute rc-top-0 rc-left-0 rc-bottom-0 rc-right-0 rc-m-auto rc-h-full rc-w-full rc-grid rc-place-content-center rc-bg-white rc-bg-opacity-40">
          <LoadingIcon loading />
          <h4>Loading content manager...</h4>
        </div>
      )}
    </div>
  );
}

export default ContentPage;
